import React, { CSSProperties, ReactElement, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { Chevron } from '../icons/Chevron'
import { RADOM_COLORS } from '../util/Constants'
import { ScaleIn } from './Animations'
import { InputLabel } from './Input'

import { useOnClickOutside } from 'usehooks-ts'

export const DropdownContext = React.createContext({
  closeDropdown: () => { }
})

const DropdownWrapper = styled.div<{ disabled?: boolean }>`
  user-select: none;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ disabled }) => disabled ? 0.5 : 1};
  position: relative;
`

const DropdownInner = styled.div<{ open: boolean, transparent?: boolean, disabled?: boolean, $iconTrigger?: boolean }>`
  ${({ $iconTrigger }) => $iconTrigger
    ? `
      border: 0;
      background: none;
      border-radius: 10px;
      padding: 7px 10px;
      min-height: unset;
      height: fit-content;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: 0.18s ease background-color, 0.18s ease opacity;

      :hover {
        background: #f4f4f5;
      }

      :active {
        opacity: 0.72;
      }
    `
    : `
      border: 1px solid;
      box-sizing: border-box;
      min-height: 44px;
      padding: 8px 12px;
      border-radius: 12px;
      transition: 0.18s ease all;
      display: flex;
      flex-direction: row;
      align-items: center;
      column-gap: 10px;
      justify-content: space-between;
      height: 100%;
  `}

  ${({ open, transparent, $iconTrigger }) => !$iconTrigger && `
    background-color: ${transparent || open ? '#ffffff' : RADOM_COLORS.INPUT_DEFAULT};
    border-color: ${transparent ? 'transparent' : open ? 'rgba(17, 17, 17, 0.22)' : '#d8dde7'};
    ${open && !transparent ? 'box-shadow: 0 0 0 3px rgba(17, 17, 17, 0.05);' : ''}
  `}

  ${({ open, transparent, disabled, $iconTrigger }) => !$iconTrigger && `
    :hover {
      background-color: ${!open && !transparent ? '#ffffff' : 'unset'};
      ${!open && !transparent && !disabled ? 'border-color: #bfc6d2;' : ''}
    }

    :active {
      opacity: 0.75;
    }
  `}
`

const DropdownContent = styled.div<{ visible: boolean }>`
  cursor: auto;
  display: ${props => props.visible ? 'block' : 'none'};
  position: fixed;
  z-index: 120;
`

const DropdownContentInner = styled.div<{ overflow?: string }>`
  max-height: 40vh;
  max-width: calc(100vw - 24px);
  min-width: 100%;
  animation: ${ScaleIn} 0.2s;
  transform-origin: 50% 0;
  background-color: white;
  border: 1px solid #e6e9ef;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.12);
  border-radius: 16px;
  ${({ overflow }) => overflow ? `overflow: ${overflow}` : 'overflow: auto'}
`

export const DropdownItem = styled.div`
  color: #282828;
  cursor: pointer;
  font-size: 14px;
  padding: 9px 10px;
  transition: 0.2s ease all;
  opacity: 1;
  width: 100%;
  box-sizing: border-box;

  :hover {
    background-color: #f5f6f7;
  }

  :active {
    opacity: 0.7;
  }
`

export const DropdownLink = styled(Link)`
  cursor: pointer;
  padding: 9px 10px;
  transition: 0.2s ease all;
  opacity: 1;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  display: block;
  text-decoration: none;
  color: black;
  
  :hover {
    background-color: #f5f6f7;
  }
  
  :active {
    opacity: 0.5;
  }
`

export const DropdownContentHeader = styled.div`
  display: flex;
  width: 100%;
  line-height: 12px;
  align-items: center;
  justify-content: space-between;
`

export const DropdownContentBody = styled.div`
  overflow: scroll;
`

export const DropdownSearchContainer = styled.div`
  background: #fff;
  border-bottom: 1px solid #eef1f6;
  padding: 10px;
  position: sticky;
  top: 0;
  z-index: 2;
`

export const DropdownTextInput = styled.input<{ value: string }>`
  background-color: transparent;
  width: 100%;
  border: none;
  outline: none;
  flex-grow: 1;
  padding: 12px;
  color: #20242d;
  font-family: var(--font-sans);
  font-size: 13px;

  ::placeholder {
    color: #9aa3b2;
  }
`

type AlignmentPreference = 'left' | 'right'

interface IProps {
  preferredAlignment?: AlignmentPreference
  selectedContent: React.ReactNode
  dropdownContent: React.ReactNode
  onClose?: () => any
  onCloseFn?: (fn: any) => any
  transparent?: boolean
  whiteChevron?: boolean
  noChevron?: boolean
  iconTrigger?: boolean
  disabled?: boolean
  overflow?: string
  onClick?: () => void
  outerStyle?: CSSProperties
  innerStyle?: CSSProperties
  containerStyle?: CSSProperties
  matchTriggerWidth?: boolean
}

interface IState {
  open: boolean
  top: number
  left: number
  right?: number
}

export default class Dropdown extends React.Component<IProps, IState> {
  dropdownWrapper: HTMLDivElement | null = null
  dropdownContent!: HTMLDivElement
  state: IState = {
    open: false,
    top: 0,
    left: 0,
    right: undefined
  }

  componentDidUpdate = (prevProps: IProps): void => {
    if (prevProps.dropdownContent !== this.props.dropdownContent) {
      this.repositionDropdown()
    }
  }

  onMouseUp = (e: MouseEvent): void => {
    if (!this.dropdownWrapper || !this.state.open) {
      return
    }

    const isClickInDropdown = this.dropdownWrapper === e.target || this.dropdownWrapper.contains(e.target as any)
    if (!isClickInDropdown) {
      this.toggleDropdown(false)
    }
  }

  componentDidMount = (): void => {
    window.addEventListener('mouseup', this.onMouseUp)
    window.addEventListener('click', this.onMouseUp)

    if (this.props.onCloseFn) {
      this.props.onCloseFn(() => this.toggleDropdown(false))
    }
  }

  componentWillUnmount(): void {
    window.removeEventListener('mouseup', this.onMouseUp)
    window.removeEventListener('click', this.onMouseUp)
  }

  toggleDropdown = (open = !this.state.open): void => {
    if (this.props.disabled) {
      return
    }

    if (open) {
      document.addEventListener('wheel', this.repositionDropdown)
      window.addEventListener('resize', this.repositionDropdown)
    } else {
      document.removeEventListener('wheel', this.repositionDropdown)
      window.removeEventListener('resize', this.repositionDropdown)
    }

    if (this.state.open && !open && this.props.onClose) {
      this.props.onClose()
    }

    const nextState: IState = {
      open,
      top: this.state.top,
      left: this.state.left,
      right: this.state.right
    }
    if (open && this.dropdownWrapper) {
      const wrapper = this.dropdownWrapper.getBoundingClientRect()
      nextState.top = wrapper.bottom + 5
      nextState.left = Math.max(12, Math.min(wrapper.x, window.innerWidth - wrapper.width - 12))
      nextState.right = undefined
    }

    this.setState(nextState, () => {
      if (open) {
        requestAnimationFrame(this.repositionDropdown)
      }
      this.props.onClick?.()
    })
  }

  repositionDropdown = (): void => {
    if (!this.dropdownWrapper || !this.dropdownContent || !this.state.open) {
      return
    }

    const wrapper = this.dropdownWrapper.getBoundingClientRect()
    const rect = this.dropdownContent.getBoundingClientRect()
    const renderedWidth = this.props.matchTriggerWidth
      ? wrapper.width
      : Math.max(
        rect.width,
        this.dropdownContent.scrollWidth,
        (this.dropdownContent.firstElementChild as HTMLElement | null)?.scrollWidth ?? 0
      )

    const bottomAlign = wrapper.height + wrapper.top + 5
    const topAlign = wrapper.y - rect.height - 5

    let top = bottomAlign
    if (bottomAlign + rect.height > window.innerHeight && topAlign >= 0) {
      // Position dropdown above dropdownWrapper if it's at the bottom of the screen and there's space above
      top = topAlign
    } else if (bottomAlign + rect.height > window.innerHeight && topAlign < 0) {
      // If there's not enough space above or below the dropdownWrapper, just center the dropdown vertically
      top = window.innerHeight / 2 - rect.height / 2
    }

    if (this.props.preferredAlignment === 'right') {
      this.setState({
        top,
        left: 0,
        right: Math.max(12, window.innerWidth - (wrapper.x + wrapper.width))
      })
      return
    }

    let left = wrapper.x
    if (left + renderedWidth > window.innerWidth) {
      // Position dropdown to the left of the dropdownWrapper if it's going off the screen to the right
      left = wrapper.x - renderedWidth + wrapper.width
    }

    left = Math.max(12, Math.min(left, window.innerWidth - renderedWidth - 12))

    this.setState({ top, left, right: undefined })
  }

  render(): React.ReactNode {
    return (
      <DropdownWrapper
        style={this.props.containerStyle}
        ref={dropdownWrapper => { if (dropdownWrapper) this.dropdownWrapper = dropdownWrapper }}
        disabled={this.props.disabled}>
        <DropdownInner
          transparent={this.props.transparent}
          $iconTrigger={this.props.iconTrigger}
          open={this.state.open}
          onClick={() => this.toggleDropdown()}
          disabled={this.props.disabled}
          style={this.props.outerStyle}>
          {this.props.selectedContent}
          {
            !this.props.noChevron &&
            <Chevron fill={this.props.whiteChevron ? 'white' : 'black'} style={{
              opacity: 0.3,
              transition: '0.2s ease all',
              transform: this.state.open ? 'rotate(180deg)' : 'rotate(0deg)',
              minWidth: 15
            }} />
          }
        </DropdownInner>
        {
          this.state.open &&
          <DropdownContent
            style={{
              minWidth: this.dropdownWrapper?.clientWidth,
              width: this.props.matchTriggerWidth ? this.dropdownWrapper?.clientWidth : undefined,
              top: this.state.top,
              left: this.state.right === undefined ? this.state.left : undefined,
              right: this.state.right
            }}
            ref={r => {
              if (!r || r === this.dropdownContent) return
              new ResizeObserver(this.repositionDropdown).observe(r)
              this.dropdownContent = r
              this.repositionDropdown()
            }}
            visible={this.state.open}
          >
            <DropdownContext.Provider value={{ closeDropdown: () => this.toggleDropdown(false) }}>
              <DropdownContentInner overflow={this.props.overflow} style={this.props.innerStyle}>
                {this.props.dropdownContent}
              </DropdownContentInner>
            </DropdownContext.Provider>
          </DropdownContent>
        }
      </DropdownWrapper>
    )
  }
}

type DropdownWithLabelProps = IProps & {
  label: string
}

export const DropdownWithLabel =
  ({ label, selectedContent, dropdownContent, onCloseFn }: DropdownWithLabelProps): ReactElement => {
    return <>
      <InputLabel>{label}</InputLabel>
      <Dropdown selectedContent={selectedContent} dropdownContent={dropdownContent} onCloseFn={onCloseFn} />
    </>
  }

interface INewDropdownProps {
  open: boolean
  preferredAlignment?: AlignmentPreference
  selectedContent: React.ReactNode
  dropdownContent: React.ReactNode
  transparent?: boolean
  whiteChevron?: boolean
  noChevron?: boolean
  disabled?: boolean
  overflow?: string
  outerStyle?: CSSProperties
  innerStyle?: CSSProperties
  onClick: () => void
  onClickOutside: () => void
  matchTriggerWidth?: boolean
}

export const NewDropdown = ({ open, disabled, transparent, ...props }: INewDropdownProps): ReactElement => {
  const dropdownWrapperRef = useRef<HTMLDivElement>(null)
  const dropdownContentRef = useRef<HTMLDivElement>(null)
  const [top, setTop] = useState(0)
  const [left, setLeft] = useState(0)
  const [right, setRight] = useState<number | undefined>(undefined)

  useOnClickOutside(dropdownContentRef, props.onClickOutside)

  useEffect(() => {
    repositionDropdown()
  }, [props.dropdownContent])

  useEffect(() => {
    if (!dropdownContentRef.current) {
      return
    }

    new ResizeObserver(repositionDropdown).observe(dropdownContentRef.current)
    repositionDropdown()
  }, [dropdownContentRef])

  const repositionDropdown = (): void => {
    if (!dropdownWrapperRef.current || !dropdownContentRef.current || !open) {
      return
    }

    const wrapper = dropdownWrapperRef.current.getBoundingClientRect()
    const rect = dropdownContentRef.current.getBoundingClientRect()
    const renderedWidth = props.matchTriggerWidth
      ? wrapper.width
      : Math.max(
        rect.width,
        dropdownContentRef.current.scrollWidth,
        (dropdownContentRef.current.firstElementChild as HTMLElement | null)?.scrollWidth ?? 0
      )

    const bottomAlign = wrapper.height + wrapper.top + 5
    const topAlign = wrapper.y - rect.height - 5

    let top = bottomAlign
    if (bottomAlign + rect.height > window.innerHeight && topAlign >= 0) {
      // Position dropdown above dropdownWrapper if it's at the bottom of the screen and there's space above
      top = topAlign
    } else if (bottomAlign + rect.height > window.innerHeight && topAlign < 0) {
      // If there's not enough space above or below the dropdownWrapper, just center the dropdown vertically
      top = window.innerHeight / 2 - rect.height / 2
    }

    if (props.preferredAlignment === 'right') {
      setTop(top)
      setLeft(0)
      setRight(Math.max(12, window.innerWidth - (wrapper.x + wrapper.width)))
      return
    }

    let left = wrapper.x
    if (left + renderedWidth > window.innerWidth) {
      // Position dropdown to the left of the dropdownWrapper if it's going off the screen to the right
      left = wrapper.x - renderedWidth + wrapper.width
    }

    left = Math.max(12, Math.min(left, window.innerWidth - renderedWidth - 12))

    setTop(top)
    setLeft(left)
    setRight(undefined)
  }

  return (
    <DropdownWrapper
      ref={dropdownWrapperRef}
      disabled={disabled}
    >
      <DropdownInner
        transparent={transparent}
        open={open}
        onClick={props.onClick}
        disabled={disabled}
        style={props.outerStyle}
      >
        {props.selectedContent}
        {
          !props.noChevron &&
          <Chevron
            fill={props.whiteChevron ? 'white' : 'black'}
            style={{
              opacity: 0.3,
              transition: '0.2s ease all',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              minWidth: 15
            }}
          />
        }
      </DropdownInner>
      {
        open &&
        <DropdownContent
          style={{
            minWidth: dropdownWrapperRef.current?.clientWidth,
            width: props.matchTriggerWidth ? dropdownWrapperRef.current?.clientWidth : undefined,
            top,
            left: right === undefined ? left : undefined,
            right
          }}
          ref={dropdownContentRef}
          visible={open}
        >
          <DropdownContentInner overflow={props.overflow} style={props.innerStyle}>
            {props.dropdownContent}
          </DropdownContentInner>
        </DropdownContent>
      }
    </DropdownWrapper>
  )
}
